import { useState } from "react";

// Phase E.2 — Deadline calendar month grid.
// Strict aesthetic: ivy / brass / bone only, squared corners, no shadows.
// Chips capped at 3 per day, "+N more" popover on click.
export default function DeadlineCalendar({ deadlines, onDownload }) {
  const [cursor, setCursor] = useState(() => {
    const d = new Date();
    return { y: d.getFullYear(), m: d.getMonth() };
  });
  const [openDay, setOpenDay] = useState(null);
  const today = new Date();
  const todayIso = today.toISOString().slice(0, 10);
  const firstDay = new Date(cursor.y, cursor.m, 1);
  const startDow = firstDay.getDay();
  const daysInMonth = new Date(cursor.y, cursor.m + 1, 0).getDate();
  const prevMonth = new Date(cursor.y, cursor.m, 0);
  const prevMonthDays = prevMonth.getDate();

  const prev = () => setCursor((c) => (c.m === 0 ? { y: c.y - 1, m: 11 } : { y: c.y, m: c.m - 1 }));
  const next = () => setCursor((c) => (c.m === 11 ? { y: c.y + 1, m: 0 } : { y: c.y, m: c.m + 1 }));

  const byDate = {};
  (deadlines || []).forEach((d) => {
    const key = (d.date || d.due_date || "").slice(0, 10);
    if (!key) return;
    if (!byDate[key]) byDate[key] = [];
    byDate[key].push(d);
  });

  const cells = [];
  // Leading dimmed days from previous month
  for (let i = 0; i < startDow; i++) {
    const day = prevMonthDays - startDow + 1 + i;
    cells.push({ day, dim: true });
  }
  for (let d = 1; d <= daysInMonth; d++) {
    const iso = `${cursor.y}-${String(cursor.m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
    cells.push({ day: d, iso, items: byDate[iso] || [], isToday: iso === todayIso });
  }
  // Trailing dimmed days
  let trailing = 1;
  while (cells.length % 7) cells.push({ day: trailing++, dim: true });

  const monthName = firstDay.toLocaleString("en-US", { month: "long", year: "numeric" });
  const monthCount = (deadlines || []).filter((d) =>
    ((d.date || d.due_date || "").slice(0, 10)).startsWith(`${cursor.y}-${String(cursor.m + 1).padStart(2, "0")}`),
  ).length;

  const label = (d) => `${d.school || "Personal"}${d.type ? " · " + d.type : ""}`;

  return (
    <div className="mt-6" data-testid="deadlines-calendar">
      <div className="flex items-center justify-between mb-3">
        <button onClick={prev} data-testid="cal-prev" className="text-[11px] uppercase tracking-[0.18em] text-stone hover:text-ivy px-2 py-1">← Prev</button>
        <div className="font-serif text-xl text-ivy tracking-tight">{monthName}</div>
        <button onClick={next} data-testid="cal-next" className="text-[11px] uppercase tracking-[0.18em] text-stone hover:text-ivy px-2 py-1">Next →</button>
      </div>
      <div className="grid grid-cols-7 border-t border-l border-stone/40" data-testid="deadlines-calendar-grid">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
          <div key={d} className="border-r border-b border-stone/40 bg-bone-dim/40 px-2 py-1.5 text-[10px] uppercase tracking-[0.2em] text-brass font-semibold">{d}</div>
        ))}
        {cells.map((c, i) => (
          <div
            key={i}
            className={`relative border-r border-b border-stone/40 min-h-[96px] p-1.5 ${
              c.dim ? "bg-bone-dim/30 text-stone/60" : "bg-bone"
            } ${c.isToday ? "border-l-2 border-l-ivy" : ""}`}
            data-testid={c.iso ? `cal-cell-${c.iso}` : undefined}
          >
            <div className={`text-[10px] ${c.dim ? "text-stone/60" : c.isToday ? "text-ivy font-semibold" : "text-stone"}`}>{c.day}</div>
            {!c.dim && (
              <div className="mt-1 space-y-0.5">
                {(c.items || []).slice(0, 3).map((d) => (
                  <button
                    key={d.deadline_id || d.item_id}
                    onClick={() => onDownload?.(d)}
                    title={label(d)}
                    className="w-full text-left border border-brass/50 bg-brass/10 hover:bg-brass/20 px-1.5 py-0.5 text-[10px] text-ivy truncate block"
                    data-testid={`cal-chip-${d.deadline_id || d.item_id}`}
                  >
                    {label(d)}
                  </button>
                ))}
                {(c.items || []).length > 3 && (
                  <button
                    onClick={() => setOpenDay(openDay === c.iso ? null : c.iso)}
                    className="text-[10px] text-brass hover:text-ivy px-1"
                    data-testid={`cal-more-${c.iso}`}
                  >
                    +{c.items.length - 3} more
                  </button>
                )}
                {openDay === c.iso && (c.items || []).length > 3 && (
                  <div
                    className="absolute z-20 top-full left-0 mt-1 border border-stone/50 bg-bone p-3 min-w-[180px]"
                    data-testid={`cal-popover-${c.iso}`}
                  >
                    <div className="text-[10px] uppercase tracking-[0.18em] text-brass mb-2">{c.iso}</div>
                    <div className="space-y-1">
                      {c.items.map((d) => (
                        <button
                          key={d.deadline_id || d.item_id}
                          onClick={() => { onDownload?.(d); setOpenDay(null); }}
                          className="w-full text-left border border-brass/40 bg-brass/5 hover:bg-brass/20 px-2 py-1 text-[11px] text-ivy block"
                        >
                          {label(d)}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
      {monthCount === 0 && (
        <div className="mt-3 border border-stone/30 bg-bone-dim/30 px-3 py-2.5 text-[11px] text-stone italic">
          Nothing scheduled this month.
        </div>
      )}
    </div>
  );
}
